/* oxlint-disable no-nested-ternary */
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type Header,
  type SortingState,
} from '@tanstack/react-table';
import { Action, ButtonGroup, cn, Icon, Input } from 'corso-admin-ui';
import { useId, useState } from 'react';
import './types';

const alignClass = (align: 'left' | 'right' | undefined) =>
  align === 'right' ? 'text-right' : 'text-left';

const renderPrimitiveCell = (value: unknown) => {
  if (value === null || value === undefined) return '—';
  if (value instanceof Date) return value.toLocaleDateString();
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return `${value}`;
  }
  return null;
};

function SortIndicator<T>({ header }: { header: Header<T, unknown> }) {
  const direction = header.column.getIsSorted();
  return (
    <Icon
      icon={direction === 'desc' ? 'chevronDown' : 'chevronUp'}
      className={cn('size-3', direction ? 'opacity-100' : 'opacity-25')}
    />
  );
}

export function DataTable<T extends { id: string | number }>({
  data,
  columns,
  onRowClick,
  filterable = false,
  pageSize = 20,
  defaultSort,
  hidePagination = false,
}: {
  data: T[];
  columns: ColumnDef<T>[];
  onRowClick?: (row: T) => void;
  filterable?: boolean;
  pageSize?: number | false;
  defaultSort?: SortingState;
  hidePagination?: boolean;
}) {
  const filterId = useId();
  const [globalFilter, setGlobalFilter] = useState('');

  const table = useReactTable({
    data,
    columns,
    defaultColumn: { cell: ({ getValue }) => renderPrimitiveCell(getValue()) },
    getRowId: (row) => `${row.id}`,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: filterable ? getFilteredRowModel() : undefined,
    getPaginationRowModel:
      pageSize === false ? undefined : getPaginationRowModel(),
    initialState: {
      sorting: defaultSort,
      ...(pageSize === false ? {} : { pagination: { pageIndex: 0, pageSize } }),
    },
    state: filterable ? { globalFilter } : undefined,
    onGlobalFilterChange: filterable ? setGlobalFilter : undefined,
  });

  const { rows } = table.getRowModel();
  const total = table.getFilteredRowModel().rows.length;
  const { pageIndex } = table.getState().pagination;
  const effectivePageSize =
    pageSize === false ? total : table.getState().pagination.pageSize;
  const showPagination =
    pageSize !== false && total > effectivePageSize && !hidePagination;

  return (
    <div className="flex flex-col gap-3">
      {filterable && (
        <Input
          id={filterId}
          label="Filter"
          labelVisuallyHidden
          type="search"
          placeholder="Filter…"
          value={globalFilter}
          onChange={(event) => setGlobalFilter(event.target.value)}
        />
      )}
      <div className="overflow-auto rounded-md border border-neutral-200 bg-white">
        <table className="min-w-full divide-y divide-neutral-200">
          <thead className="bg-neutral-50">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const align = header.column.columnDef.meta?.align;
                  const canSort = header.column.getCanSort();
                  return (
                    <th
                      key={header.id}
                      scope="col"
                      className={cn(
                        'px-4 py-2 text-xs font-semibold tracking-wider whitespace-nowrap text-neutral-700 uppercase',
                        alignClass(align),
                      )}
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className={cn(
                            'inline-flex items-center gap-1 uppercase hover:text-neutral-900',
                            align === 'right' && 'flex-row-reverse',
                          )}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          <SortIndicator header={header} />
                        </button>
                      ) : (
                        flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-neutral-200">
            {rows.map((row) => (
              <tr
                key={row.id}
                className={cn(
                  'hover:bg-neutral-50',
                  onRowClick && 'cursor-pointer',
                )}
                onClick={
                  onRowClick ? () => onRowClick(row.original) : undefined
                }
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={cn(
                      'px-4 py-2 text-xs text-neutral-800',
                      alignClass(cell.column.columnDef.meta?.align),
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showPagination && (
        <div className="flex items-center justify-between px-1 text-xs text-neutral-600">
          <span>
            Showing {pageIndex * effectivePageSize + 1}–
            {Math.min((pageIndex + 1) * effectivePageSize, total)} of {total}
          </span>
          <ButtonGroup horizontal>
            <Action
              icon="chevronLeft"
              accessibilityLabel="Previous page"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            />
            <Action
              icon="chevronRight"
              accessibilityLabel="Next page"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            />
          </ButtonGroup>
        </div>
      )}
    </div>
  );
}
